import { ScanLine } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router';

import { appNavStyles as styles } from './AppNav.styles';
import { type AppNavPresenterProps, type NavItem } from './AppNav.types';

import { cn } from '@/lib/cn';

/** Mobile et tablette — barre basse, avec le bouton de scan au centre. */
export function AppNavTabBar({
  items,
  onScan,
  scanHint,
  className,
}: Readonly<AppNavPresenterProps>) {
  const { t } = useTranslation('nav');
  const middle = Math.ceil(items.length / 2);

  // Icônes seules : à 375 px, cinq libellés se coupaient ou passaient sur deux
  // lignes. Le nom reste porté par `aria-label`, donc le lien garde exactement
  // le même nom accessible qu'avec un texte visible.
  const renderItem = ({ to, labelKey, icon: Icon }: NavItem) => (
    <NavLink
      key={to}
      to={to}
      aria-label={t(labelKey)}
      className={cn(styles.item({ layout: 'tabbar' }))}
    >
      <Icon aria-hidden className={styles.icon({ layout: 'tabbar' })} />
    </NavLink>
  );

  return (
    <nav
      aria-label={t('aria.primary')}
      className={cn(styles.root({ layout: 'tabbar' }), className)}
    >
      {items.slice(0, middle).map(renderItem)}

      <span className="relative grid place-items-center">
        {scanHint}

        <button
          type="button"
          onClick={onScan}
          aria-label={t('scan')}
          className={cn(styles.scanButton(), scanHint && 'animate-scan-halo')}
        >
          <ScanLine aria-hidden className="size-6" />
        </button>
      </span>

      {items.slice(middle).map(renderItem)}
    </nav>
  );
}
